import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaShare, FaArrowLeft, FaCopy, FaCheck } from 'react-icons/fa';
import { useKidsFlasher } from '../context/KidsFlasherContext';
import ChampionCard from '../components/ChampionCard';
import ErrorMessage from '../components/ErrorMessage';

const getBoostLevelColor = (score = 1) => {
  switch (score) {
    case 1: return 'text-green-400';
    case 2: return 'text-yellow-300';
    case 3: return 'text-orange-400';
    case 4: return 'text-red-400';
    case 5: return 'text-red-600';
    default: return 'text-gray-400';
  }
};

const ResultsPage = ({ isShared = false }) => {
  const { playerData, error, fetchPlayerData, fetchSharedData, generateShareLink } = useKidsFlasher();
  const { riotId, shareId } = useParams();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      if (!isInitialLoad) return; // Prevent subsequent loads
      
      if (isShared && shareId) {
        await fetchSharedData(shareId);
      } else if (riotId) {
        await fetchPlayerData(decodeURIComponent(riotId));
      }
      setIsInitialLoad(false);
    };
    
    loadData();
  }, [isShared, shareId, riotId, fetchSharedData, fetchPlayerData, isInitialLoad]);

  const handleShare = async () => {
    setIsGeneratingLink(true);
    const newShareId = await generateShareLink();
    
    if (newShareId) {
      const url = `${window.location.origin}/shared/${newShareId}`;
      setShareUrl(url);
    }
    setIsGeneratingLink(false);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (error) {
    return <ErrorMessage message={error} />;
  }

  if (!playerData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-white text-xl"
        >
          Loading...
        </motion.div>
      </div>
    );
  }

  const { flashOnD = [], flashOnF = [] } = playerData.champions;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700 py-8 px-4 sm:px-6">
      <div className="max-w-6xl mx-auto">
        <motion.header
          className="mb-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center mb-4 md:mb-0">
            <motion.button
              onClick={() => navigate('/')}
              className="mr-4 p-2 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition duration-300"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FaArrowLeft />
            </motion.button>
            <div>
              <h1 className="text-2xl md:text-3xl text-white font-bold">
                {playerData.summonerName}
                <span className="text-blue-300 text-lg ml-1">#{playerData.tag}</span>
              </h1>
              <p className="text-blue-300 text-sm mt-1">
                Flash Analysis Report
              </p>
            </div>
          </div>
          
          <div>
            {!isShared && !shareUrl && (
              <motion.button
                onClick={handleShare}
                disabled={isGeneratingLink}
                className="py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition duration-300 flex items-center gap-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaShare />
                {isGeneratingLink ? 'Generating...' : 'Generate Share Link'}
              </motion.button>
            )}
            
            {shareUrl && (
              <div className="flex items-center bg-slate-800 bg-opacity-60 p-2 rounded-lg">
                <input 
                  type="text" 
                  value={shareUrl} 
                  readOnly 
                  className="bg-transparent text-white text-sm flex-grow mr-2 focus:outline-none"
                />
                <motion.button
                  onClick={copyToClipboard}
                  className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {copied ? <FaCheck /> : <FaCopy />}
                </motion.button>
              </div>
            )}
          </div>
        </motion.header>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
          <motion.section
            className="bg-black bg-opacity-40 backdrop-filter backdrop-blur-lg p-4 sm:p-6 rounded-xl"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-xl font-bold text-white mb-6 flex items-center">
              <span className="inline-block w-8 h-8 bg-yellow-500 text-black font-bold rounded-full mr-3 flex items-center justify-center">D</span>
              Flash on D
              <span className="text-sm font-normal text-yellow-400 ml-2">
                ({flashOnD.length} champions)
              </span>
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {flashOnD.sort((a, b) => b.games - a.games).map((champion, index) => (
                <ChampionCard 
                  key={champion.id} 
                  champion={champion} 
                  index={index} 
                  flashKey="D"
                />
              ))}
              
              {flashOnD.length === 0 && (
                <p className="text-blue-300 col-span-2 text-center py-8">
                  No champions with Flash on D found.
                </p>
              )}
            </div>
          </motion.section>
          
          <motion.section
            className="bg-black bg-opacity-40 backdrop-filter backdrop-blur-lg p-4 sm:p-6 rounded-xl"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-xl font-bold text-white mb-6 flex items-center">
              <span className="inline-block w-8 h-8 bg-blue-500 text-black font-bold rounded-full mr-3 flex items-center justify-center">F</span>
              Flash on F
              <span className="text-sm font-normal text-blue-400 ml-2">
                ({flashOnF.length} champions)
              </span>
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {flashOnF.sort((a, b) => b.games - a.games).map((champion, index) => (
                <ChampionCard 
                  key={champion.id} 
                  champion={champion} 
                  index={index} 
                  flashKey="F"
                />
              ))}
              
              {flashOnF.length === 0 && (
                <p className="text-blue-300 col-span-2 text-center py-8">
                  No champions with Flash on F found.
                </p>
              )}
            </div>
          </motion.section>
        </div>
        
        <motion.div
          className="mt-8 bg-black bg-opacity-40 backdrop-filter backdrop-blur-lg p-4 sm:p-6 rounded-xl relative"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          <div className="flex flex-col lg:flex-row justify-between items-start gap-4">
            <div className="flex-grow space-y-4">
              <h2 className="text-xl sm:text-2xl font-bold text-white">Analysis Results</h2>
              
              <p className="text-base sm:text-lg text-white">
                Flash Key Distribution: <span className="font-semibold text-blue-300">{playerData.summonerName}</span> uses Flash on 
                {(() => {
                  const dGames = flashOnD.reduce((sum, champ) => sum + champ.games, 0);
                  const fGames = flashOnF.reduce((sum, champ) => sum + champ.games, 0);
                  const totalGames = dGames + fGames;
                  const dPercentage = Math.round((dGames / totalGames) * 100);
                  const fPercentage = Math.round((fGames / totalGames) * 100);
                  return dGames > fGames ? 
                    ` D ${dPercentage}% ` : 
                    ` F ${fPercentage}% `;
                })()}
                of the time
              </p>
              
              <div className="space-y-2">
                <p className={`text-xl font-bold ${getBoostLevelColor(playerData.boostAnalysis?.score)}`}>
                  {playerData.boostAnalysis?.message || "Analysis not available"}
                </p>
                <p className="text-gray-300">
                  {playerData.boostAnalysis?.description || "Not enough data to perform analysis"}
                </p>
              </div>
            </div>

            {playerData.boostAnalysis?.score > 1 && playerData.boostAnalysis?.details && (
              <motion.div 
                className="bg-red-900 bg-opacity-20 p-3 rounded-lg border border-red-500/30 w-full lg:max-w-[300px] shrink-0"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <h3 className="text-red-400 font-semibold mb-1">Suspicious Patterns Detected</h3>
                <ul className="text-gray-300 text-sm space-y-1">
                  {playerData.boostAnalysis.details.hasSignificantHighWinRate && (
                    <li className="flex items-center">
                      <span className="text-red-400 mr-2">•</span>
                      High win rate sustained over many games
                    </li>
                  )}
                  {playerData.boostAnalysis.details.hasLowSampleHighWinRate && (
                    <li className="flex items-center">
                      <span className="text-red-400 mr-2">•</span>
                      Unusually high win rate in recent games
                    </li>
                  )}
                  {playerData.boostAnalysis.details.hasExtremeWinRateDiff && (
                    <li className="flex items-center">
                      <span className="text-red-400 mr-2">•</span>
                      Extreme win rate improvement on same champions
                    </li>
                  )}
                  {playerData.boostAnalysis.details.secondaryWinRate > 
                   playerData.boostAnalysis.details.primaryWinRate + 10 && (
                    <li className="flex items-center">
                      <span className="text-red-400 mr-2">•</span>
                      Significant performance improvement with different Flash key
                      (+{Math.round(playerData.boostAnalysis.details.secondaryWinRate - 
                      playerData.boostAnalysis.details.primaryWinRate)}% win rate)
                    </li>
                  )}
                </ul>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ResultsPage;